// The one place that knows the kinds (spec 2026-10-24 link kinds). Adding a kind = one file in kinds/ plus one line
// in KINDS; screens ask kindOf(item) and its caps, never the type or provider.
import { audioFile, radio } from "./kinds/stream";
import { embed } from "./kinds/embed";
import { photo } from "./kinds/photo";
import { soundcloud } from "./kinds/soundcloud";
import { video } from "./kinds/video";
import { youtube } from "./kinds/youtube";
import type { IconName, PlayableItem, PlayerKind } from "./types";

/** Tried in order; within a provider or type the first whose `matches` holds wins (a live stream is a radio). */
export const KINDS: readonly PlayerKind[] = [youtube, soundcloud, radio, audioFile, photo, video];

/** Unknown providers and types: the hosted-player embed. */
export const DEFAULT_KIND: PlayerKind = embed;

/** A link row before it resolved (the composer's empty link). */
export const PENDING_LINK_ICON: IconName = "play";

/**
 * Provider first (trimmed, any case; `stream` is a radio when live, else an audio file, whatever the type), then type
 * (rows without a provider: before the backfill, or an old image wrote them), then the hosted embed. The same rule as
 * Android's PlayerRegistry.kindOf; both are checked against __tests__/kind-facts.json.
 */
export function kindOf(item: PlayableItem): PlayerKind {
  const provider = item.provider?.trim().toLowerCase() || null;
  if (provider) {
    const byProvider = KINDS.find((k) => k.provider === provider && k.matches(item));
    if (byProvider) return byProvider;
  }
  return KINDS.find((k) => k.types.includes(item.type) && k.matches(item)) ?? DEFAULT_KIND;
}

/** The item's kind when the registry has one for it; null where kindOf falls back to the hosted embed (an unknown type). */
export function knownKindOf(item: PlayableItem): PlayerKind | null {
  const kind = kindOf(item);
  return kind === DEFAULT_KIND ? null : kind;
}

/** A queue item type room-stc may send (spec 2026-10-19 §3.1): a kind's stored type that a queue holds. */
export const isQueueType = (v: unknown): v is string =>
  typeof v === "string" && KINDS.some((k) => k.caps.queueable && k.types.includes(v));

/**
 * The item a cast slot plays when it carries only its `mediaType` (older servers, HTTP slot lists): MUSIC plays live
 * (no seek, safe for both radio and file), no media type is an old YouTube cast.
 */
export function slotFallbackItem(mediaType: string | null | undefined): PlayableItem {
  const kind =
    KINDS.find((k) => k.slot?.fallback && k.slot.mediaType === mediaType) ?? KINDS.find((k) => k.slot?.defaultFallback) ?? DEFAULT_KIND;
  return { type: kind.types[0] ?? "", live: kind.caps.live };
}

/** The item plays in the wall dock (a row's play button; Prev / Next land on it). */
export function isDockPlayable(item: PlayableItem): boolean {
  const kind = kindOf(item);
  return kind.caps.dockable && kind.playUrl(item) != null && kind.canPlay(item);
}

/** The item can be in a cast's play queue (room-stc SnapshotItems), URL length aside. */
export function isQueueReady(item: PlayableItem): boolean {
  const kind = kindOf(item);
  return kind.caps.queueable && kind.queueReady(item);
}

/** Photos and videos: the grid, carousel and full-screen viewer rather than a row. */
export const isGalleryItem = (item: PlayableItem): boolean => kindOf(item).caps.gallery;
export const isPhotoItem = (item: PlayableItem): boolean => kindOf(item).surface === "photo";
export const isVideoItem = (item: PlayableItem): boolean => kindOf(item).surface === "video";

/** An item's kind and whether this browser can play it, asked once. */
export interface ResolvedItem {
  kind: PlayerKind;
  playable: boolean;
}
export function resolveItem(item: PlayableItem): ResolvedItem {
  const kind = kindOf(item);
  return { kind, playable: kind.canPlay(item) };
}

/** Same key = the same player instance carries on with the next item; a new one when the surface or live rule changes. */
export const playerKey = (kind: PlayerKind): string => `${kind.surface}:${kind.caps.live ? "live" : "timed"}`;
